import React from "react"
import styled from "styled-components"
import useMapData, { ApiMedia } from "../hooks/queries/use-map-data"
import { CgClose, CgRadioCheck, CgRadioChecked } from "react-icons/cg"
import useTrackedPoints from "../hooks/queries/tracked-points"
import ReactMarkdown from "react-markdown"
import ParallelogramButton from "../styles/ParallelogramButton"
import { FaLink } from "react-icons/fa"
import { toast } from "react-hot-toast"
import { useAuth } from "../hooks/context/auth"

import { outsideBorderThickness } from "../styles"

const PointPopup = ({ clickInfo, handleClose, setOnDisplayCategories, categoryMap: propCategoryMap, mediaMap: propMediaMap }: any) => {
  const { user } = useAuth()
  const fallback = useMapData(); const categoryMap = propCategoryMap || fallback.categoryMap; const mediaMap = propMediaMap || fallback.mediaMap
  const { trackedPointIds, setTrackedPoints } = useTrackedPoints()

  if (!clickInfo) return null

  // todo: support multiple media, support youtube
  const medias: ApiMedia[] = []
  try {
    const mediaIds = JSON.parse(clickInfo?.medias)
    mediaIds.forEach((mediaId: number) => {
      const media = mediaMap[mediaId]
      if (media) medias.push(media)
    })
  } catch (e) {}

  const media = medias.filter((m) => m.type === "image")[0]

  return (
    <Root>
      <PopupAbove>
        <ParallelogramButton
          onClick={async () => {
            // save tracked points (works with or without login via localStorage fallback)
            let newTrackedPointIds
            if (trackedPointIds.includes(clickInfo.id)) {
              newTrackedPointIds = trackedPointIds.filter((id: number) => id !== clickInfo.id)
            } else {
              newTrackedPointIds = [...trackedPointIds, clickInfo.id]
            }
            await setTrackedPoints({ pointIds: newTrackedPointIds })
            if (!user) {
              toast("Tracked! Log in to save across devices.", { icon: "📍" })
            }
          }}
        >
          <div>
            <span style={{ marginRight: 5 }}>Found</span>
            {trackedPointIds.includes(clickInfo.id) ? <CgRadioChecked size={14} /> : <CgRadioCheck size={14} />}
          </div>
        </ParallelogramButton>
        <ParallelogramButton onClick={handleClose}>
          <div>
            <CgClose />
          </div>
        </ParallelogramButton>
      </PopupAbove>
      {media?.url && <img src={media.url} key={media.url} alt={media.url} />}
      <div className="content">
        <h2 className="h5">
          {clickInfo.name}
          <FaLink
            onClick={async () => {
              // @ts-ignore
              const { state } = await navigator.permissions.query({ name: "clipboard-write" })
              if (state === "granted" || state === "prompt") {
                await navigator.clipboard.writeText(`${window.location.href}#${clickInfo.id}`)
                toast.success("Link copied to clipboard!")
              }
            }}
            cursor="pointer"
            style={{ marginLeft: 5 }}
            size={13}
          />
        </h2>
        <Category className="h6" onClick={() => setOnDisplayCategories([clickInfo.categoryId])}>
          {categoryMap[clickInfo.categoryId]?.name || "Unknown"}
        </Category>
        <ReactMarkdown>{clickInfo.description}</ReactMarkdown>
      </div>
    </Root>
  )
}

export const Category = styled.button`
  width: min-content;
  white-space: nowrap;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.8);
  color: black;
  border: 1px solid transparent;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 0 10px;
  text-transform: uppercase;

  &:hover {
    color: #fff;
    background: #000;
    border: 1px solid white;
  }
`

const Root = styled.div`
  position: relative;
  min-width: 200px;
  max-width: 300px;

  img {
    width: 100%;
  }

  .content {
    padding: 10px;
  }

  background-color: ${({ theme }) => theme.colors.background};
  border: 2px solid ${({ theme }) => theme.colors.primaryFaded};
  ${({ theme }) => theme.glowColor && `box-shadow: 0 0 12px ${theme.glowColor}, inset 0 0 12px rgba(0, 240, 255, 0.05);`}

  :after {
    content: "";
    position: absolute;
    top: -${outsideBorderThickness}px;
    left: -${outsideBorderThickness}px;
    right: -${outsideBorderThickness}px;
    bottom: -${outsideBorderThickness}px;
    border: 3px solid ${(props) => props.theme.colors.primaryHighlight};

    border-left: 3px dashed ${(props) => props.theme.colors.primary};
    border-bottom: 3px dashed ${(props) => props.theme.colors.primary};

    pointer-events: none;
    overflow: hidden;
    // https://stackoverflow.com/questions/7324722/cut-corners-using-css
    // https://blog.logrocket.com/how-to-create-fancy-corners-in-css/
    clip-path: polygon(0 10%, 10% 0, 90% 0, 100% 10%, 100% 90%, 90% 100%, 10% 100%, 0% 90%, 0% 10%);
    z-index: -1;
    ${({ theme }) => theme.glowColor && `box-shadow: 0 0 16px ${theme.glowColor};`}
  }
`

export const PopupAbove = styled.div`
  position: absolute;
  top: -25px;
  right: 0;
  z-index: 1;
`

export default PointPopup
